"use client";
import React, { useEffect, useState } from "react";
import GooglePlay from "../assets/icons/googleplay";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import Apple from "@/app/assets/icons/apple";
import InCredLogoDark from "@/app/assets/images/icm-logo-dark.png";
import Image from "next/image";
import Facebook from "../assets/icons/facebook";
import Instagram from "../assets/icons/instagram";
import LinkedIn from "../assets/icons/linkedin";
import X from "../assets/icons/x";
import Youtube from "../assets/icons/youtube";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { FACEBOOK, INSTAGRAM, LINKEDIN, X_TWITTER, YOUTUBE } from "@/app/utils/constants";
import { DirectPaths } from "@/config/routes";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import useMobile from "../hooks/use-mobile";
import { usePathname } from "next/navigation";

interface FooterProps {
    active?: string;
}

export const Footer: React.FC<FooterProps> = ({ active = "Neutral" }) => {
    const pathName = usePathname();
    const [isInvestNowSection, setIsInvestNowSection] = useState(false);

    const productLinks = [
        {
            url: `${process.env.ICM_URL}/unlisted-shares` || DirectPaths.UNLISTED_SHARES || "#",
            heading: "Unlisted Shares",
            events: "US_Footer",
        },
        { url: `${process.env.ICM_BONDS_URL}/login` || "#", heading: "Bonds" },
        { url: process.env.ICM_FD_URL || "#", heading: "FDs", events: "FDs_Footer" },
        { url: `${process.env.ICM_URL}/refer`, heading: "Refer", events: "Refer_Footer" },
        { url: `${process.env.ICM_BONDS_URL}/login` || "#", heading: "MLDs" },
        { url: `${process.env.ICM_URL}/bullion/gold/24-karat-gold` || "#", heading: "Gold", new: true },
    ];

    const socialLinks = [
        { url: FACEBOOK, heading: <Facebook className="text-white" /> },
        { url: INSTAGRAM, heading: <Instagram className="text-white" /> },
        { url: LINKEDIN, heading: <LinkedIn className="text-white" /> },
        { url: X_TWITTER, heading: <X className="text-white" /> },
        { url: YOUTUBE, heading: <Youtube className="text-white" /> },
    ];

    const ICMMediaLinks = [
        { url: process.env.ICM_ABOUT || "#", heading: "About us" },
        { url: process.env.ICM_MEDIA || "#", heading: "Media", target: "_blank" },
        { url: process.env.ICM_CAREERS || "#", heading: "Career" },
        {
            url: `${process.env.ICM_URL}${DirectPaths.ANNOUNCEMENT}` || "#",
            heading: "Important Announcement",
        },
        { url: `${process.env.ICM_LEARN}` || "#", heading: "Learn", target: "_blank" },
    ];

    const ICMQuickLinks = [
        { url: `${process.env.ICM_URL}${DirectPaths.CALCULATOR}` || "#", heading: "Calculator" },
        {
            url: `${process.env.ICM_URL}${DirectPaths.TERMS_AND_CONDITIONS}` || "#",
            heading: "Terms & Conditions",
        },
        {
            url: `${process.env.ICM_URL}${DirectPaths.PRIVACY_POLICY}` || "#",
            heading: "Privacy Policy",
        },
        {
            url: `${process.env.ICM_URL}${DirectPaths.INVESTOR_RELATIONS}` || "#",
            heading: "Investor Relations",
        },
        { url: `${process.env.ICM_URL}${DirectPaths.CONTACT}` || "#", heading: "Contact Us" },
        {
            url:
                active === "Stocks"
                    ? `${process.env.ICM_URL}${DirectPaths.UE_DISCLAIMER}` || "#"
                    : `${process.env.ICM_URL}${DirectPaths.DISCLAIMER}` || "#",
            heading: "Disclaimer",
        },
    ];

    interface FooterLink {
        url: string;
        heading: string | React.ReactNode;
        events?: string;
        target?: string;
    }

    const FooterLinkList = (section: FooterLink[]) => {
        return (
            <>
                {section.map((link, index) => (
                    <a key={index} href={link.url} target={link.target ?? ""} className="flex">
                        <p
                            onClick={() => {
                                setAnalyticsEvent(link.url, link.events || "");
                            }}
                        >
                            {link.heading}
                        </p>
                    </a>
                ))}
            </>
        );
    };

    const { isMobile } = useMobile(); // ✅ Call the hook here

    useEffect(() => {
        if (
            isMobile &&
            (pathName.includes("/unlisted-shares") ||
                pathName.includes("/refer") ||
                pathName.includes("/bullion"))
        ) {
            setIsInvestNowSection(true);
        } else {
            setIsInvestNowSection(false);
        }
    }, [isMobile, pathName]);

    return (
        <footer
            className={
                "mx-auto bg-neutral-900 px-5 py-10 md:px-[140px] md:py-[100px] " +
                (isInvestNowSection ? " pb-16" : "")
            }
        >
            {" "}
            <div className="md:container">
                <div className="mb-14 flex flex-col justify-between md:flex-row">
                    <div className="mb-6 flex flex-col gap-6 md:mb-0 md:w-[270px] md:gap-10">
                        <Image src={InCredLogoDark} alt="InCred Logo" width={130} height={130} />

                        <p className="text-base font-medium text-neutral-300">
                            What you do with your money today can change how you live tomorrow.
                            That’s the power of investing right. We have built InCred Money to give
                            everyone a chance to do that.
                        </p>

                        <div className="flex items-center justify-start gap-6">
                            {FooterLinkList(socialLinks)}
                        </div>

                        <div className="sec-bullion mb-6 mt-2 flex items-center justify-start gap-2 md:-mb-4 md:mt-4 md:min-w-[120%]">
                            <Image
                                src={
                                    process.env.ICM_PUBLIC_ASSETS_URL +
                                    "images/v2/webp/MMTC-PAMP-Logo.webp"
                                }
                                alt=""
                                width={50}
                                height={50}
                            />
                            <span className="text-sm text-neutral-300">
                                Digital Gold/Silver on InCred Money powered by MMTC-PAMP
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-row flex-wrap gap-10 text-neutral-300 md:gap-[100px]">
                        <div>
                            <h5 className="mb-[18px] text-sm font-semibold text-white md:mb-[50px] md:text-lg">
                                InCred Money
                            </h5>
                            <div className="flex flex-col gap-s7 text-xs md:gap-s9 md:text-base">
                                {FooterLinkList(ICMMediaLinks)}
                            </div>
                        </div>
                        <div className="flex-1">
                            <h5 className="mb-[18px] text-sm font-semibold text-white md:mb-[50px] md:text-lg">
                                Products
                            </h5>
                            <div className="flex flex-col gap-s7 text-xs md:gap-s9 md:text-base">
                                {FooterLinkList(productLinks)}
                            </div>
                        </div>
                        <div>
                            <h5 className="mb-[18px] text-sm font-semibold text-white md:mb-[50px] md:text-lg">
                                Quick Link
                            </h5>
                            <div className="flex flex-col gap-s7 text-xs md:gap-s9 md:text-base">
                                {FooterLinkList(ICMQuickLinks)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col justify-between gap-6 border-b-2 border-t-2 border-neutralOpacityLight-10 py-6 md:flex-row md:items-center md:gap-0">
                    <p className="text-base font-medium text-neutral-300">
                        &copy; 2022-2025 InCred Money, All rights reserved. Built with love in
                        India.
                    </p>
                    <div className="flex gap-4">
                        <RegularButton
                            icon={<Apple className="text-white hover:text-black" />}
                            showRightIcon={false}
                            size={ButtonSize.Medium}
                            variant={ButtonVariant.SecondaryDefault}
                            className="w-full max-w-full border-neutralOpacityLight-30 text-white hover:text-black md:max-w-fit"
                            onClick={() => window.open(process.env.ICM_APPLE_APP_URL)}
                            label="iOS"
                        />
                        <RegularButton
                            showRightIcon={false}
                            icon={<GooglePlay className="text-white hover:text-black" />}
                            size={ButtonSize.Medium}
                            variant={ButtonVariant.SecondaryDefault}
                            onClick={() => window.open(process.env.ICM_PLAYSTORE_URL)}
                            className="w-full max-w-full border-neutralOpacityLight-30 text-white hover:text-black md:max-w-fit"
                            label="Android"
                        />
                    </div>
                </div>

                <Accordion type="single" collapsible={true} className="w-full max-w-full">
                    <AccordionItem value={"Disclaimer"}>
                        <AccordionTrigger
                            showIcon={true}
                            className="text-left text-sm font-semibold leading-[32px] text-white md:text-xl"
                        >
                            Disclaimer
                        </AccordionTrigger>
                        <AccordionContent className="text-lg font-medium text-neutral-400">
                            {active === "Stocks" && (
                                <div>
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        The information and data provided on the InCred Money
                                        platform concerning unlisted equities are solely for
                                        informational purposes and should not be interpreted as a
                                        recommendation or solicitation to invest. InCred Money and
                                        its representatives are not SEBI-registered research
                                        analysts or advisors. Any research, analysis, or information
                                        presented on this platform does not constitute investment
                                        advice or a recommendation by InCred Money or its
                                        affiliates. Investors are strongly encouraged to perform
                                        their own thorough research and due diligence before making
                                        any investment decisions. Any decision to invest or not to
                                        invest shall be at your own discretion. Unlisted equities
                                        carry a higher risk profile compared to listed securities,
                                        including but not limited to liquidity risk, regulatory
                                        risk, and market volatility. Potential investors should be
                                        aware of these risks and consider them carefully. It is
                                        highly recommended that investors consult with their own
                                        financial advisors to understand the suitability of such
                                        investments in the context of their personal financial
                                        situation, investment objectives, and risk tolerance.
                                        Additionally, investors should read and fully understand the
                                        detailed risk profile and disclosures attached to the
                                        unlisted equities before proceeding with any investment.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        The information mentioned on the platform is based on
                                        publicly available data and to the best of our knowledge,
                                        does not constitute insider information.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Pre-IPO shares are unlisted securities, but this should not
                                        be interpreted as InCred Money guaranteeing or confirming
                                        that an IPO will take place for these shares. The decision
                                        to proceed with an IPO is entirely at the discretion of the
                                        issuing company, which may choose not to go public.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Unlisted shares are subject to a lock-in period of six
                                        months from the date of listing.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        InCred Money is the brand name of Alpha Fintech Private
                                        Limited with its registered office at The Capital, Unit No
                                        1203, 12th floor, B Wing, Plot No. C-70, G Block, Bandra
                                        Kurla Complex, Mumbai – 400 051.
                                        <br /> Unless otherwise mentioned, Alpha Fintech Private
                                        Limited will be the counterparty (seller) while making your
                                        investment.{" "}
                                    </p>
                                </div>
                            )}
                            {active === "Neutral" || active === "Calculator" ? (
                                <div>
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Fixed Deposits are offered by Banks/NBFCs and are regulated
                                        by the Reserve Bank of India. Alpha Fintech Private Limited,
                                        under the brand name of InCred Money, is acting in the
                                        capacity of Sub Agent/Referral Partner for distribution of
                                        Fixed Deposits of Banks and NBFCs. The funds transferred by
                                        the customer for the Fixed Deposits are directly credited to
                                        the regulated entity offering the Fixed Deposit product.
                                        Fixed Deposits are offered to Resident Indians above the age
                                        of 18 who have a PAN & an Aadhaar. Interest rates are
                                        subject to change.{" "}
                                        <a
                                            href={process.env.TERMS}
                                            target="_blank"
                                            className="underline"
                                        >
                                            *T&C apply.
                                        </a>{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        The information and data provided on the InCred Money
                                        platform concerning unlisted equities are solely for
                                        informational purposes and should not be interpreted as a
                                        recommendation or solicitation to invest. InCred Money and
                                        its representatives are not SEBI-registered research
                                        analysts or advisors. Any research, analysis, or information
                                        presented on this platform does not constitute investment
                                        advice or a recommendation by InCred Money or its
                                        affiliates. Investors are strongly encouraged to perform
                                        their own thorough research and due diligence before making
                                        any investment decisions. Any decision to invest or not to
                                        invest shall be at your own discretion. Unlisted equities
                                        carry a higher risk profile compared to listed securities,
                                        including but not limited to liquidity risk, regulatory
                                        risk, and market volatility. Potential investors should be
                                        aware of these risks and consider them carefully. It is
                                        highly recommended that investors consult with their own
                                        financial advisors to understand the suitability of such
                                        investments in the context of their personal financial
                                        situation, investment objectives, and risk tolerance.
                                        Additionally, investors should read and fully understand the
                                        detailed risk profile and disclosures attached to the
                                        unlisted equities before proceeding with any investment.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        The information mentioned on the platform is based on
                                        publicly available data and to the best of our knowledge,
                                        does not constitute insider information.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Pre-IPO shares are unlisted securities, but this should not
                                        be interpreted as InCred Money guaranteeing or confirming
                                        that an IPO will take place for these shares. The decision
                                        to proceed with an IPO is entirely at the discretion of the
                                        issuing company, which may choose not to go public.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Unlisted shares are subject to a lock-in period of six
                                        months from the date of listing.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        InCred Money is the brand name of Alpha Fintech Private
                                        Limited with its registered office at The Capital, Unit No
                                        1203, 12th floor, B Wing, Plot No. C-70, G Block, Bandra
                                        Kurla Complex, Mumbai – 400 051. Unless otherwise mentioned,
                                        Alpha Fintech Private Limited will be the counterparty
                                        (seller) while making your investment.{" "}
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        *Corporate Bonds transactions are executed on Biddeasy.com.
                                        Biddeasy is Online Bond platform provider and registered
                                        with BSE and NSE in the name of Oro Financial Consultants
                                        Pvt Ltd.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Oro Financial Consultants Pvt Ltd is the Stock broker entity
                                        operating in cash and debt segment with BSE and Debt segment
                                        with NSE having SEBI registration number: INZ000312534,
                                        Registered as an Online Bond Platform Provider in BSE
                                        Ltd.(Member Code: 6821) and National Stock Exchange of India
                                        Limited (Member Code: 90389){" "}
                                    </p>
                                    <br />
                                    {/* <p className="text-sm font-medium leading-[19.6px]">
                                    Important Links:{" "}
                                    <a href="https://www.sebi.gov.in/" target="_blank">
                                        SEBI
                                    </a>{" "}
                                    |{" "}
                                    <a href="https://www.bseindia.com/" target="_blank">
                                        BSE
                                    </a>{" "}
                                    |{" "}
                                    <a href="https://www.nseindia.com/" target="_blank">
                                        NSE
                                    </a>{" "}
                                    |{" "}
                                    <a href="https://nsdl.co.in/" target="_blank">
                                        NSDL
                                    </a>
                                </p> */}
                                </div>
                            ) : (
                                <></>
                            )}
                            {active === "FD" && (
                                <div className="mb-5">
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Fixed Deposits are offered by Banks/NBFCs and are regulated
                                        by the Reserve Bank of India. Alpha Fintech Private Limited,
                                        under the brand name of InCred Money, is acting in the
                                        capacity of Sub Agent/Referral Partner for distribution of
                                        Fixed Deposits of Banks and NBFCs. The funds transferred by
                                        the customer for the Fixed Deposits are directly credited to
                                        the regulated entity offering the Fixed Deposit product.
                                        Fixed Deposits are offered to Resident Indians above the age
                                        of 18 who have a PAN & an Aadhaar. Interest rates are
                                        subject to change.{" "}
                                        <a
                                            href={process.env.TERMS}
                                            target="_blank"
                                            className="underline"
                                        >
                                            *T&C apply.
                                        </a>{" "}
                                    </p>
                                </div>
                            )}
                            {active === "Bullion" && (
                                <div>
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Alpha Fintech Private Limited, operating under the brand
                                        name “InCred Money,“ provides a digital platform that
                                        facilitates the purchase and sale of “Digital Gold/Silver.“
                                        The contractual relationship in relation to the Digital
                                        Gold/Silver is exclusively between the Client and MMTC-PAMP
                                        India Private Limited (“MMTC“). InCred Money acts strictly
                                        as a referral partner and technology facilitator, providing
                                        a platform to these transactions. The Client&lsquo;s use of
                                        the InCred Money platform for any Digital Gold/Silver
                                        transaction constitutes an express agreement and
                                        acknowledgement of this arrangement.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        By initiating any transaction for Digital Gold/Silver on the
                                        InCred Money platform, the Client hereby represents and
                                        warrants that they have read, understood, and unequivocally
                                        agree to be bound by the terms and conditions of service, as
                                        well as the disclaimers of representation and warranties, as
                                        stipulated under{" "}
                                        <a
                                            href="http://www.incredmoney.com/terms-of-use"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline"
                                        >
                                            incredmoney.com/terms-of-use
                                        </a>
                                        . It is the Client&lsquo;s sole responsibility to review and
                                        comprehend the agreement it signs with MMTC prior to
                                        undertaking any transaction. The initiation of a transaction
                                        shall be deemed as the Client&lsquo;s conclusive acceptance
                                        of the said terms and disclaimers.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        The Client acknowledges and agrees that all aspects of the
                                        Digital Gold/SIlver, including but not limited to its
                                        delivery, quality, quantity, purity, and custodial
                                        arrangements, are the exclusive responsibility and liability
                                        of MMTC. InCred Money shall not be held liable, at any point
                                        in time or under any circumstances, for the quality,
                                        delivery, or any other matter pertaining to the Digital
                                        Gold/SIlver transacted between the Client and MMTC. InCred
                                        Money makes no representations or warranties, express or
                                        implied, with respect to the Digital Gold/Silver offered by
                                        MMTC.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Any complaints or grievances related to the quality,
                                        delivery, or other aspects of the Digital Gold/Silver must
                                        be addressed directly with MMTC. InCred Money may, at its
                                        sole discretion, assist in facilitating communication
                                        between the Client and MMTC as a courtesy, but is under no
                                        obligation to do so. InCred Money assumes no liability or
                                        responsibility arising from such facilitation. Resolution of
                                        any disputes or grievances is exclusively between the Client
                                        and MMTC.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        InCred Money hereby discloses that it receives remuneration
                                        from MMTC for the facilitation of Digital Gold/Silver
                                        transactions conducted through its platform, pursuant to a
                                        pre-existing commercial arrangement between InCred Money and
                                        MMTC. Investment in digital gold & silver is at your own
                                        risk & discretion. Please read the terms, conditions and
                                        disclaimers on the InCred Money & MMTC-PAMP web platform
                                        prior to any purchase.
                                    </p>
                                    <br />
                                    <p className="text-sm font-medium leading-[19.6px]">
                                        Investment in digital gold & silver is at your own risk &
                                        discretion. Please read the terms, conditions and
                                        disclaimers on the InCred Money & MMTC-PAMP web platform
                                        prior to any purchase.
                                    </p>
                                </div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        </footer>
    );
};
